"use client"

import { useState, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MapPin, Loader2 } from "lucide-react"
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

interface LocationResult {
  display_name: string
  lat: string
  lon: string
  address: {
    city?: string
    town?: string
    village?: string
    country?: string
  }
}

interface LocationSearchProps {
  onLocationSelect: (location: {
    name: string
    country: string
    latitude: number
    longitude: number
  }) => void
  value?: string
  className?: string
}

export function LocationSearch({ onLocationSelect, value, className }: LocationSearchProps) {
  const [searchQuery, setSearchQuery] = useState(value || "")
  const [results, setResults] = useState<LocationResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const searchLocation = async () => {
      if (searchQuery.length < 3) {
        setResults([])
        return
      }

      setIsLoading(true)
      try {
        // Using Nominatim (OpenStreetMap) - free, no API key required
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?` +
            `q=${encodeURIComponent(searchQuery)}&` +
            `format=json&` +
            `addressdetails=1&` +
            `limit=5`,
          {
            headers: {
              "User-Agent": "SurfSpotsApp/1.0", // Required by Nominatim
            },
          },
        )
        const data = await response.json()
        setResults(data)
      } catch (error) {
        console.error("Error searching location:", error)
      } finally {
        setIsLoading(false)
      }
    }

    const debounce = setTimeout(searchLocation, 300)
    return () => clearTimeout(debounce)
  }, [searchQuery])

  const handleSelect = (result: LocationResult) => {
    // Extract the most specific location name from display_name
    // display_name format is typically: "Name, City, Region, Country"
    // We want the first part (most specific location)
    let locationName = ""
    
    if (result.display_name) {
      // Split by comma and take the first part (most specific location)
      const parts = result.display_name.split(",").map(p => p.trim())
      locationName = parts[0] || ""
    }
    
    // Fallback to address hierarchy if display_name parsing fails
    if (!locationName) {
      locationName = result.address.village || result.address.town || result.address.city || ""
    }
    
    const country = result.address.country || ""

    setSearchQuery(`${locationName}, ${country}`)
    onLocationSelect({
      name: locationName,
      country: country,
      latitude: Number.parseFloat(result.lat),
      longitude: Number.parseFloat(result.lon),
    })
    setOpen(false)
    setResults([]) // Clear results after selection
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="location-search">Location *</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none z-10" />
            <Input
              id="location-search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                if (e.target.value.length >= 3) {
                  setOpen(true)
                } else {
                  setOpen(false)
                  setResults([])
                }
              }}
              onFocus={() => {
                if (searchQuery.length >= 3 && results.length > 0) {
                  setOpen(true)
                }
              }}
              placeholder="Search for a city or village..."
              className={`pl-10 ${className}`}
              required
            />
            {isLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[400px] p-0 bg-white dark:bg-gray-950" 
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandList>
              {isLoading ? (
                <div className="p-4 text-center text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
                  Searching...
                </div>
              ) : results.length === 0 && searchQuery.length >= 3 ? (
                <CommandEmpty>No locations found. Try a different search term.</CommandEmpty>
              ) : (
                <CommandGroup>
                  {results.map((result, index) => (
                    <CommandItem
                      key={`${result.lat}-${result.lon}-${index}`}
                      onSelect={() => handleSelect(result)}
                      className="flex items-start gap-2 cursor-pointer"
                    >
                      <MapPin className="w-4 h-4 mt-1 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{result.display_name}</div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
